/**
 * SignatureConfirmationModal — eDMS Design System
 *
 * THE central UX of the system. Used for every signature action:
 * AUTHORSHIP, REVIEW, QA_APPROVAL, READ_AND_UNDERSTOOD, WITHDRAWAL,
 * NO_CHANGE_PERIODIC, DELEGATION, REASSIGNMENT, MGMT_ENDORSEMENT,
 * WAIVER_QA, WAIVER_DOC_CTRL, MIGRATION_ATTESTATION, FAILOVER_AUTH,
 * DSAR_FULFILLMENT, BREACH_NOTIFICATION.
 *
 * Source: drafts/31_ux_design_system_spec.md §4.1
 *
 * REGULATORY-CRITICAL DESIGN RULES (do not relax without QA sign-off):
 *
 *   1. Cannot be dismissed by ESC key (deliberate friction; per §4.1).
 *   2. Cannot be dismissed by outside-click (per §4.1).
 *   3. Cancel button is LESS visually prominent than Sign.
 *   4. Sign button is PROMINENT and uses "this is irrevocable" framing.
 *   5. Initials match is case- AND punctuation-insensitive ("M.A" == "MA").
 *      Initials are OPTIONAL per `requireInitials` (default true): low-stakes
 *      review/ack steps may confirm identity via the session without typing
 *      initials (QA-approved tiering — approval/critical meanings keep the
 *      gate). Typed initials are not a Part-11 requirement — see drafts/57 §E-sig.
 *   6. Attestation text rendered verbatim per attestation_text_version.
 *   7. Two-step: when initials are required this modal handles step 1 (identity
 *      confirmation); step 2 is the 2FA challenge (handled by parent flow).
 *   8. 60-second challenge token will be re-issued if user lingers — UI does
 *      not auto-submit; user must click Continue.
 *   9. NO animation on this modal — gravitas required (per §9.2).
 */

import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { clsx } from 'clsx';
import { Button } from '../Button/Button';
import styles from './SignatureConfirmationModal.module.css';

export type SignatureMeaning =
  | 'AUTHORSHIP'
  | 'REVIEW'
  | 'QA_APPROVAL'
  | 'READ_AND_UNDERSTOOD'
  | 'WITHDRAWAL'
  | 'NO_CHANGE_PERIODIC'
  | 'DELEGATION'
  | 'REASSIGNMENT'
  | 'MGMT_ENDORSEMENT'
  | 'WAIVER_QA'
  | 'WAIVER_DOC_CTRL'
  | 'MIGRATION_ATTESTATION'
  | 'FAILOVER_AUTH'
  | 'DSAR_FULFILLMENT'
  | 'BREACH_NOTIFICATION';

export interface SignatureConfirmationModalProps {
  /** Whether modal is open. */
  isOpen: boolean;

  /** Signer's full name as captured (per SIG-011 User_Name_History at signing time). */
  signerFullName: string;

  /** Signer's job title for context display. */
  signerJobTitle: string;

  /** Signer's expected initials (case- + punctuation-insensitive match). */
  expectedInitials: string;

  /**
   * Whether the signer must type matching initials to confirm. Default true.
   * Pass false for low-stakes review/acknowledgement steps — the signer confirms
   * via the existing session identity (no initials field). Approval + critical
   * meanings should keep this true. See drafts/57 §E-signature.
   */
  requireInitials?: boolean;

  /**
   * Whether the signer must re-enter their account password to confirm (ESG-02).
   * Default false. When true, the password is collected here and handed to
   * `onConfirmIdentity` for the caller to send to the server, which re-verifies
   * it against the IdP at sign-time (Part-11 "something you know"). The password
   * is never stored client-side beyond this modal's lifetime.
   */
  requirePassword?: boolean;

  /** Signature meaning enum value. */
  signatureMeaning: SignatureMeaning;

  /** Human-readable target description, e.g., "Document SOP-PROD-014 v3.0". */
  target: string;

  /** Server-side UTC timestamp at challenge issuance (formatted). */
  utcTimestamp: string;

  /** Localized rendering of UTC in user's timezone (display only). */
  localTimeDisplay?: string;

  /**
   * Attestation text — rendered verbatim. Must come from server (per
   * attestation_text_version mechanism; do not hardcode in UI).
   */
  attestationText: string;

  /** Attestation text version (audited per I18N-003). */
  attestationTextVersion: number;

  /** User cancels — should call parent's cancel handler. */
  onCancel: () => void;

  /**
   * User confirms identity. Called with the typed initials (validated to match)
   * and, when `requirePassword` is set, the entered password for the caller to
   * pass to the server for re-verification.
   */
  onConfirmIdentity: (initials: string, password?: string) => void;
}

export function SignatureConfirmationModal({
  isOpen,
  signerFullName,
  signerJobTitle,
  expectedInitials,
  requireInitials = true,
  requirePassword = false,
  signatureMeaning,
  target,
  utcTimestamp,
  localTimeDisplay,
  attestationText,
  attestationTextVersion,
  onCancel,
  onConfirmIdentity,
}: SignatureConfirmationModalProps) {
  const { t } = useTranslation();
  const [initials, setInitials] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const initialsRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  // Focus management: on open, focus title (informational, then user can Tab to input).
  // On close, return focus to triggering element.
  useEffect(() => {
    if (isOpen) {
      previousFocusRef.current = document.activeElement as HTMLElement;
      // Slight delay to let modal render
      requestAnimationFrame(() => titleRef.current?.focus());
      // Lock body scroll
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = originalOverflow;
        // Restore focus
        previousFocusRef.current?.focus?.();
      };
    }
    return undefined;
  }, [isOpen]);

  // Per §4.1: ESC does NOT close. Trap focus inside modal.
  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'Escape') {
      // Deliberate: do NOT close on ESC. Inform user via subtle inline message.
      setError(t('signature.escDisabled', { defaultValue: 'Press Cancel to close.' }));
      event.preventDefault();
      return;
    }
    if (event.key === 'Tab') {
      // Focus trap — simplified: cycle within modal's interactive elements.
      const focusable = event.currentTarget.querySelectorAll<HTMLElement>(
        'button:not(:disabled), input:not(:disabled), [tabindex]:not([tabindex="-1"])'
      );
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        last.focus();
        event.preventDefault();
      } else if (!event.shiftKey && document.activeElement === last) {
        first.focus();
        event.preventDefault();
      }
    }
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    // Validate initials when required. Case- AND punctuation-insensitive
    // ("M.A" / "M. A" / "MA" all match) — the prior whitespace-only strip
    // rejected "M.A" and generated support tickets.
    if (requireInitials) {
      const normalize = (s: string) => s.trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
      const normalized = normalize(initials);
      const expected = normalize(expectedInitials);
      if (!normalized) {
        setError(t('signature.initialsRequired', { defaultValue: 'Type your initials to confirm.' }));
        initialsRef.current?.focus();
        return;
      }
      if (normalized !== expected) {
        setError(
          t('signature.initialsMismatch', {
            defaultValue: 'Initials do not match the signer profile. Please re-enter.',
          })
        );
        initialsRef.current?.focus();
        return;
      }
    }
    // ESG-02 — require the account password for real sign-time re-authentication.
    if (requirePassword && !password.trim()) {
      setError(t('signature.passwordRequired', { defaultValue: 'Enter your account password to sign.' }));
      passwordRef.current?.focus();
      return;
    }
    setError(null);
    onConfirmIdentity(requireInitials ? initials : '', requirePassword ? password : undefined);
  }

  if (!isOpen) return null;

  return (
    <div
      className={styles.overlay}
      // Do NOT close on outside click (per §4.1).
      // No onClick on overlay.
      role="presentation"
      data-testid="signature-modal-overlay"
    >
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="signature-modal-title"
        aria-describedby="signature-modal-attestation"
        onKeyDown={handleKeyDown}
      >
        <header className={styles.header}>
          <h2
            id="signature-modal-title"
            ref={titleRef}
            tabIndex={-1}
            className={styles.title}
          >
            {t(`signature.meaning.${signatureMeaning}.title`, {
              defaultValue: `Sign as ${signatureMeaning.replace(/_/g, ' ').toLowerCase()}`,
            })}
          </h2>
          <p className={styles.subtitle}>
            {requireInitials
              ? t('signature.stepOf', { defaultValue: 'Step 1 of 2 — Identity confirmation' })
              : t('signature.confirmStep', { defaultValue: 'Confirm completion' })}
          </p>
        </header>

        <div className={styles.body}>
          {/* Signer identity */}
          <section className={styles.section} aria-labelledby="signature-signer-heading">
            <h3 id="signature-signer-heading" className={styles.sectionHeading}>
              {t('signature.signerHeading', { defaultValue: 'You are about to sign as' })}
            </h3>
            <div className={styles.identity}>
              <p className={styles.signerName}>{signerFullName}</p>
              <p className={styles.signerJob}>{signerJobTitle}</p>
            </div>
          </section>

          {/* Purpose */}
          <section className={styles.section} aria-labelledby="signature-purpose-heading">
            <h3 id="signature-purpose-heading" className={styles.sectionHeading}>
              {t('signature.purposeHeading', { defaultValue: 'For the purpose of' })}
            </h3>
            <div className={styles.purpose}>
              <p className={styles.meaning}>{signatureMeaning.replace(/_/g, ' ')}</p>
              <p className={styles.target}>{target}</p>
            </div>
          </section>

          {/* Dated */}
          <section className={styles.section} aria-labelledby="signature-date-heading">
            <h3 id="signature-date-heading" className={styles.sectionHeading}>
              {t('signature.datedHeading', { defaultValue: 'Dated' })}
            </h3>
            <div className={styles.date}>
              <p className={styles.utcTime}>{utcTimestamp}</p>
              {localTimeDisplay && (
                <p className={styles.localTime}>{localTimeDisplay}</p>
              )}
            </div>
          </section>

          {/* Attestation — rendered verbatim from server */}
          <section
            className={styles.attestationBox}
            aria-labelledby="signature-attestation-heading"
            data-attestation-version={attestationTextVersion}
          >
            <h3 id="signature-attestation-heading" className={styles.attestationHeading}>
              {t('signature.attestationHeading', { defaultValue: 'Attestation' })}
            </h3>
            <p id="signature-modal-attestation" className={styles.attestationText}>
              {attestationText}
            </p>
          </section>

          {/* Identity confirmation */}
          <form className={styles.confirmForm} onSubmit={handleSubmit} noValidate>
            {requireInitials && (
              <>
                <label htmlFor="signature-initials" className={styles.initialsLabel}>
                  {t('signature.initialsLabel', { defaultValue: 'Type your initials to confirm' })}
                  <span aria-hidden="true" className={styles.required}>*</span>
                </label>
                <input
                  id="signature-initials"
                  ref={initialsRef}
                  type="text"
                  autoComplete="off"
                  autoCapitalize="characters"
                  maxLength={6}
                  required
                  value={initials}
                  onChange={(e) => {
                    setInitials(e.target.value);
                    if (error) setError(null);
                  }}
                  aria-invalid={error ? 'true' : undefined}
                  aria-describedby={error ? 'signature-initials-error' : undefined}
                  className={clsx(styles.initialsInput, error && styles.initialsInputError)}
                />
              </>
            )}
            {requirePassword && (
              <>
                <label htmlFor="signature-password" className={styles.initialsLabel}>
                  {t('signature.passwordLabel', { defaultValue: 'Re-enter your account password to sign' })}
                  <span aria-hidden="true" className={styles.required}>*</span>
                </label>
                <input
                  id="signature-password"
                  ref={passwordRef}
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (error) setError(null);
                  }}
                  aria-invalid={error ? 'true' : undefined}
                  aria-describedby={error ? 'signature-initials-error' : undefined}
                  className={clsx(styles.initialsInput, error && styles.initialsInputError)}
                />
              </>
            )}
            {error && (
              <p id="signature-initials-error" role="alert" className={styles.errorText}>
                {error}
              </p>
            )}

            <footer className={styles.footer}>
              <Button
                variant="secondary"
                size="lg"
                onClick={onCancel}
                type="button"
              >
                {t('signature.cancel', { defaultValue: 'Cancel' })}
              </Button>
              <Button variant="primary" size="lg" type="submit">
                {requirePassword
                  ? t('signature.applySignature', { defaultValue: 'Apply signature' })
                  : requireInitials
                  ? t('signature.continueTo2FA', { defaultValue: 'Continue to 2FA Challenge →' })
                  : t('signature.confirmReview', { defaultValue: 'Confirm review' })}
              </Button>
            </footer>
          </form>
        </div>
      </div>
    </div>
  );
}
